import MetaOverrideSet from './MetaOverrideSet';
import {NativeNetworkAdapter, WebNetworkAdapter} from "./NetworkAdapter";

const nodeFetch = require('node-fetch');

const GLOBAL_META = Symbol('facet-rest-global-meta');
const REQUEST_INTERCEPTORS = Symbol('facet-rest-request-interceptors');
const RESPONSE_INTERCEPTORS = Symbol('facet-rest-response-interceptors');
const ERROR_HANDLERS = Symbol('facet-rest-error-handlers');
const ABORT_HANDLERS = Symbol('facet-rest-abort-handlers');
const SILENT_ERROR_HANDLERS = Symbol('facet-rest-silent-error-handlers');
const PLATFORM_ADAPTER = Symbol('platformAdapter');

const fetch = typeof window !== 'undefined' ? (...args) => window.fetch(...args) : nodeFetch; // Prefer the browser's fetch over node-fetch, because the browser's fetch is integrated with openreplay
const defaultFetchImpl = (...args) => fetch(...args);

class Configuration {
  constructor(){
    if(!Configuration.instance){
     Configuration.instance = this;

     this[GLOBAL_META] = new MetaOverrideSet();
     this[REQUEST_INTERCEPTORS] = new Set();
     this[RESPONSE_INTERCEPTORS] = new Set();
     this[ERROR_HANDLERS] = new Set();
     this[ABORT_HANDLERS] = new Set()
     this[SILENT_ERROR_HANDLERS] = new Set();
     this[PLATFORM_ADAPTER] = {
       adapter: new WebNetworkAdapter({
          fetch: defaultFetchImpl,
       }),
     };
   }

   return Configuration.instance;
  }

  addSilentErrorHandler(callback) {
    if (typeof callback !== 'function') {
      throw new TypeError(`callback must be typeof function got: ${typeof callback}`)
    }

    this[SILENT_ERROR_HANDLERS].add(callback);

    return this;
  }

  removeSilentErrorHandler(callback) {
    if (this[SILENT_ERROR_HANDLERS].has(callback)) {
      this[SILENT_ERROR_HANDLERS].delete(callback);
    }
  }

  onSilentError(error) {
    for (const cb of this[SILENT_ERROR_HANDLERS]) {
      Promise.resolve().then(() => cb(error));
    }
  }

  clearSilentErrorHandlers() {
    for (const cb of this[SILENT_ERROR_HANDLERS]) {
      this[SILENT_ERROR_HANDLERS].delete(cb)
    }
  }


  onAbort(error) {
    for (const cb of this[ABORT_HANDLERS]) {
      Promise.resolve().then(() => cb(error));
    }
  }

  addAbortHandler(callback) {
    if (typeof callback !== 'function') {
      throw new TypeError(`callback must be typeof function got: ${typeof callback}`)
    }

    this[ABORT_HANDLERS].add(callback);
    return this;
  }


  addErrorHandler(callback) {
    if (typeof callback !== 'function') {
      throw new TypeError(`callback must be typeof function got: ${typeof callback}`)
    }

    this[ERROR_HANDLERS].add(callback);
    return this;
  }

  removeErrorHandler(callback) {
    if (this[ERROR_HANDLERS].has(callback)) {
      this[ERROR_HANDLERS].delete(callback);
    }
  }

  onError(error) {
    for (const cb of this[ERROR_HANDLERS]) {
      Promise.resolve().then(() => cb(error));
    }
  }

  clearErrorHandlers() {
    for (const cb of this[ERROR_HANDLERS]) {
      this[ERROR_HANDLERS].delete(cb)
    }
  }

  addGlobalMetaOverride(filterObj, override, args) {
    this[GLOBAL_META].override(filterObj, override, args);
    return this;
  };

  listGlobalMetaOverrides() {
    return this[GLOBAL_META].list()
  };

  clearGlobalMetaOverrides() {
    this[GLOBAL_META].clearOverrides();
    return this;
  };

  addRequestInterceptor(interceptor) {
    if (typeof interceptor !== 'function') {
      throw new TypeError(`callback must be typeof function got: ${typeof interceptor}`)
    }

    this[REQUEST_INTERCEPTORS].add(interceptor);

    return this;
  };

  removeRequestInterceptor(interceptor) {
    if (this[REQUEST_INTERCEPTORS].has(interceptor)) {
      this[REQUEST_INTERCEPTORS].delete(interceptor);
    }
  }

  async forEachRequestInterceptor(f) {
    for (const interceptor of this[REQUEST_INTERCEPTORS]) {
      await f(interceptor);
    }
  };

  printRequestInterceptors(prefix) {
    let count = 0;

    for (const interceptor of this[REQUEST_INTERCEPTORS]) {
      count++;
    }

    if (prefix) {
      console.log(`${prefix}: Request interceptors: ${count}`);
    } else {
      console.log(`Request interceptors: ${count}`);
    }
  }

  addResponseInterceptor(interceptor) {
    if (typeof interceptor !== 'function') {
      throw new TypeError(`callback must be typeof function got: ${typeof interceptor}`)
    }

    this[RESPONSE_INTERCEPTORS].add(interceptor);

    return this;
  };

  async forEachResponseInterceptor(f) {
    for (const interceptor of this[RESPONSE_INTERCEPTORS]) {
      await new Promise((resolve, reject) => {
        const timerId = setTimeout(() => {
          reject("This took too long");
        }, 30000);
        return f(interceptor)
          .then((res) => {
            return res;
          })
          .then((res) => {
            clearTimeout(timerId);
            resolve(res);
          })
          .catch((err) => {
            clearTimeout(timerId);
            reject(err);
          });
      })
    }
  };

  printResponseInterceptors(prefix) {
    let count = 0;

    for (const interceptor of this[RESPONSE_INTERCEPTORS]) {
      count++;
    }


    if (prefix) {
      console.log(`${prefix}: Response interceptors: ${count}`);
    } else {
      console.log(`Response interceptors: ${count}`);
    }
  }

  removeResponseInterceptor(interceptor) {
    if (this[RESPONSE_INTERCEPTORS].has(interceptor)) {
      this[RESPONSE_INTERCEPTORS].delete(interceptor);
    }
  }

  clearAllInterceptors() {
    this[REQUEST_INTERCEPTORS].clear();
    this[RESPONSE_INTERCEPTORS].clear();
    return this;
  };

  setPlatformConfig(platform, config) {
    let adapter;
    switch (platform) {
      case "web":
        adapter = new WebNetworkAdapter(config)
        break;
      case "native":
        adapter = new NativeNetworkAdapter(config)
        break;
      default:
        throw new Error(`Developer configuration error: unsupported platform type ${platform} passed to setPlatformConfig`);
    }
    this[PLATFORM_ADAPTER].adapter = adapter;
    return this;
  }

  resetPlatformConfig() {
    this[PLATFORM_ADAPTER].adapter = new WebNetworkAdapter({
      fetch: defaultFetchImpl,
    });
    return this;
  };

  getCurrentPlatformAdapter() {
    if (!this[PLATFORM_ADAPTER].adapter) {
      throw new Error('Developer configuration error: we must have a platform adapter defined');
    }
    return this[PLATFORM_ADAPTER].adapter;
  }
}

const instance = new Configuration();
Object.freeze(instance);

export default instance;
